import type {
  StrategyRdComposerConfluenceSlot,
  StrategyRdComposerEntrySlot,
  StrategyRdComposerSetupSlot,
  StrategyRdConcept,
  StrategyRdDirectionMode,
  StrategyRdInstrument,
  StrategyRdLogicPath,
  StrategyRdMaxWait,
  StrategyRdPatternPolarity,
  StrategyRdSidePolicy,
  StrategyRdSlotSide,
} from "../types/strategyRd";

const LEVEL_SETUP_IDS = new Set([
  "session_first_candle",
  "pdh_pdl",
  "pwh_pwl",
  "pmh_pml",
  "swing_hl",
  "developing_day_hl",
]);
/** ICT liquidity setups — Pool polarity uses high/low/both (same field shape as Level). */
const POOL_SETUP_IDS = new Set(["liq_sweep", "liq_run"]);

/** Catalog swing_length labels (keep in sync with STRATEGY_RD_SWING_LENGTH_SETTING). */
const SWING_LENGTH_CATALOG_LABELS: Record<string, string> = {
  "2": "2 bars",
  "3": "3 bars (default)",
  "5": "5 bars",
  "10": "10 bars",
};

function strategyRdSwingLengthCatalogLabel(value: unknown): string | null {
  const token = String(value ?? "").trim();
  if (!token) return null;
  return (
    SWING_LENGTH_CATALOG_LABELS[token] ||
    SWING_LENGTH_CATALOG_LABELS[String(Number(token))] ||
    null
  );
}

/** Catalog Min % move labels for pct_break_bar (keep in sync with STRATEGY_RD_PCT_BREAK_BAR_SETTINGS). */
const PCT_BREAK_MIN_PCT_LABELS: Record<string, string> = {
  "0.25": "0.25%",
  "0.5": "0.5%",
  "0.75": "0.75%",
  "1": "1%",
  "1.5": "1.5%",
  "2": "2%",
  "2.5": "2.5%",
  "3": "3%",
  "4": "4%",
  "5": "5%",
};

function strategyRdPctBreakMinPctLabel(value: unknown): string | null {
  const token = String(value ?? "").trim();
  if (!token) return null;
  return (
    PCT_BREAK_MIN_PCT_LABELS[token] ||
    PCT_BREAK_MIN_PCT_LABELS[String(Number(token))] ||
    null
  );
}

/** Keys BE stamps on slots for itself — never shown to the user. */
const COMPOSER_HIDDEN_PARAM_KEYS = new Set(["polarity", "entry_max_wait_hours", "setup_minutes"]);

type ComposerParamSetting = {
  key: string;
  label?: string | null;
  enum_values?: Array<{ value: unknown; label?: string | null }> | null;
};

function composerParamValueLabel(setting: ComposerParamSetting | undefined, value: unknown): string {
  const option = (setting?.enum_values || []).find((row) => {
    if (row.value === null) return value === null;
    return String(row.value) === String(value);
  });
  if (option?.label?.trim()) return option.label.trim();
  if (value === null) return "Any";
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";
  const token = String(value ?? "").trim();
  return token ? token.charAt(0).toUpperCase() + token.slice(1).replace(/_/g, " ") : "";
}

/** `allow_neutral` → "Allow neutral" when the catalog has no label. */
function composerParamKeyLabel(key: string): string {
  const text = key.replace(/_/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : key;
}

function formatComposerParamBits(
  params: Record<string, unknown> | null | undefined,
  settings?: readonly ComposerParamSetting[] | null
): string {
  if (!params) return "";
  return Object.entries(params)
    .filter(([k]) => !COMPOSER_HIDDEN_PARAM_KEYS.has(k))
    .map(([k, v]) => {
      // Catalog label + option label ("Scope Day", "Allow neutral No") — never raw keys.
      const setting = settings?.find((row) => row.key === k);
      if (setting && (setting.label || setting.enum_values?.length)) {
        const name = setting.label?.trim() || composerParamKeyLabel(k);
        const value = Array.isArray(v)
          ? v.map((item) => composerParamValueLabel(setting, item)).filter(Boolean).join(", ")
          : composerParamValueLabel(setting, v);
        return value ? `${name} ${value}` : name;
      }
      if (k === "swing_length") {
        const label = strategyRdSwingLengthCatalogLabel(v);
        return `Swing length ${label || composerParamValueLabel(undefined, v)}`;
      }
      if (k === "ma_kind") {
        const token = String(v ?? "").trim().toLowerCase();
        return token === "sma" ? "MA type SMA" : token === "ema" ? "MA type EMA" : `MA type ${composerParamValueLabel(undefined, v)}`;
      }
      if (k === "ma_length") return `MA length ${composerParamValueLabel(undefined, v)}`;
      if (k === "min_pct") {
        const label = strategyRdPctBreakMinPctLabel(v);
        return `Min % ${label || composerParamValueLabel(undefined, v)}`;
      }
      if (k === "require_vol_expand") {
        const on = v === true || v === "true" || v === 1;
        return on ? "Volume expansion Yes" : "Volume expansion No";
      }
      const value = Array.isArray(v)
        ? v.map((item) => composerParamValueLabel(undefined, item)).join(", ")
        : composerParamValueLabel(undefined, v);
      return value ? `${composerParamKeyLabel(k)} ${value}` : composerParamKeyLabel(k);
    })
    .join(", ");
}

function setupPolarityKindForId(
  setupId: string
): "pattern" | "level" | "pool" | "none" {
  const id = setupId.trim();
  if (LEVEL_SETUP_IDS.has(id)) return "level";
  // Catalog may still send buyside/sellside (pool) or high/low (level-shaped Pool).
  // Wire compact treats both as level when enums are high/low; pool when buyside.
  if (POOL_SETUP_IDS.has(id)) return "level";
  return "none";
}

/** Normalize FE/BE side aliases to long | short | both | invert. */
export function normalizeStrategyRdSlotSide(
  raw: string | null | undefined
): StrategyRdSlotSide | null {
  const token = (raw || "").trim().toLowerCase();
  if (!token) return null;
  if (token === "long" || token === "buy") {
    return "long";
  }
  if (token === "short" || token === "sell") {
    return "short";
  }
  if (token === "both" || token === "either" || token === "any") {
    return "both";
  }
  if (
    token === "invert" ||
    token === "inverted" ||
    token === "fade" ||
    token === "crossed"
  ) {
    return "invert";
  }
  // Legacy aliases still accepted for trade side only when clearly directional words.
  if (token === "bull" || token === "bullish") return "long";
  if (token === "bear" || token === "bearish") return "short";
  return null;
}

export function strategyRdMaxWaitForWire(
  raw: { value?: number | null; unit?: string | null } | null | undefined
): StrategyRdMaxWait | undefined {
  if (!raw) return undefined;
  const unit = String(raw.unit || "").trim().toLowerCase();
  if (unit === "none" || unit === "no_limit" || unit === "unlimited") return { unit: "none" };
  const value = Number(raw.value);
  if (!(value > 0)) return undefined;
  if (unit === "hours" || unit === "days" || unit === "bars") return { value, unit };
  if (unit === "hour" || unit === "day" || unit === "bar") {
    return { value, unit: `${unit}s` as "hours" | "days" | "bars" };
  }
  return undefined;
}

/**
 * Copy max_wait onto an entry object (a value, or `{unit: "none"}` for No limit),
 * or drop the key when the box is empty (BE default: 24 setup-TF bars).
 */
export function strategyRdEntryWithMaxWait<T extends object>(
  entry: T,
  source: { max_wait?: StrategyRdMaxWait | null } | null | undefined
): Omit<T, "max_wait"> & { max_wait?: StrategyRdMaxWait } {
  const wait = strategyRdMaxWaitForWire(source?.max_wait);
  const next = { ...entry } as Omit<T, "max_wait"> & { max_wait?: StrategyRdMaxWait };
  if (wait) next.max_wait = wait;
  else delete next.max_wait;
  return next;
}

/** Map composer trade side → Rules / scan side_policy. */
export function strategyRdSidePolicyFromSlotSide(
  side: string | null | undefined
): StrategyRdSidePolicy {
  const normalized = normalizeStrategyRdSlotSide(side);
  if (normalized === "long") return "long_only";
  if (normalized === "short") return "short_only";
  // both + invert: both directions book from the polarity remap.
  return "both";
}

/**
 * When Setup Trade side is Long or Short, Entry should follow (and side_policy
 * becomes long_only / short_only). Keeps Level/Pattern independent of trade side
 * while preventing Entry=Both from reopening the opposite break.
 */
export function strategyRdAlignEntrySideToSetup(
  setup: Pick<StrategyRdComposerSetupSlot, "side"> | null | undefined,
  entry: StrategyRdComposerEntrySlot
): StrategyRdComposerEntrySlot {
  const setupSide = normalizeStrategyRdSlotSide(setup?.side);
  if (setupSide !== "long" && setupSide !== "short") return entry;
  const entrySide = normalizeStrategyRdSlotSide(entry.side);
  if (entrySide === setupSide) return entry;
  return { ...entry, side: setupSide };
}

/**
 * Fade Level+Side pairs that prefer Entry=rejection (High→Short, Low→Long).
 * Classic breakout is the opposite (High→Long, Low→Short).
 */
export function strategyRdIsRejectionLevelSide(
  polarity: string | null | undefined,
  side: string | null | undefined
): boolean {
  const level = normalizeStrategyRdLevelPolarity(polarity);
  const trade = normalizeStrategyRdSlotSide(side);
  return (level === "high" && trade === "short") || (level === "low" && trade === "long");
}

/** Classic breakout Level+Side (High→Long, Low→Short). */
export function strategyRdIsBreakoutLevelSide(
  polarity: string | null | undefined,
  side: string | null | undefined
): boolean {
  const level = normalizeStrategyRdLevelPolarity(polarity);
  const trade = normalizeStrategyRdSlotSide(side);
  return (level === "high" && trade === "long") || (level === "low" && trade === "short");
}

/**
 * Align Entry side to Setup Trade side, and default Entry mode for Level setups:
 * High+Short / Low+Long → rejection; High+Long / Low+Short → break_close when leaving rejection.
 */
export function strategyRdAlignEntryToSetup(
  setup: Pick<StrategyRdComposerSetupSlot, "side" | "polarity"> | null | undefined,
  entry: StrategyRdComposerEntrySlot
): StrategyRdComposerEntrySlot {
  let next = strategyRdAlignEntrySideToSetup(setup, entry);
  // A custom entry rule is the user's own choice — never swap it for a preset.
  if (next.mode === "rule") return next;
  const side = normalizeStrategyRdSlotSide(setup?.side) || normalizeStrategyRdSlotSide(next.side);
  const polarity = setup?.polarity != null ? String(setup.polarity) : null;
  if (strategyRdIsRejectionLevelSide(polarity, side)) {
    if (next.mode !== "rejection") {
      next = { ...next, mode: "rejection" };
    }
  } else if (next.mode === "rejection" && strategyRdIsBreakoutLevelSide(polarity, side)) {
    next = { ...next, mode: "break_close" };
  }
  return next;
}

/** Pattern polarity for setups — bull | bear | both (not trade side). */
export function normalizeStrategyRdPolarity(
  raw: string | null | undefined
): StrategyRdPatternPolarity | null {
  const token = (raw || "").trim().toLowerCase();
  if (!token) return null;
  if (token === "bull" || token === "bullish") return "bull";
  if (token === "bear" || token === "bearish") return "bear";
  if (token === "both" || token === "either" || token === "any") return "both";
  return null;
}

/** Level polarity for ORB / PDH-PDL / developing H-L — high | low | both. */
export type StrategyRdLevelPolarity = "high" | "low" | "both";

export function normalizeStrategyRdLevelPolarity(
  raw: string | null | undefined
): StrategyRdLevelPolarity | null {
  const token = (raw || "").trim().toLowerCase();
  if (!token) return null;
  if (token === "high") return "high";
  if (token === "low") return "low";
  if (token === "both" || token === "either" || token === "any") return "both";
  return null;
}

/** Pool polarity for liquidity sweep — buyside | sellside | both. */
export type StrategyRdPoolPolarity = "buyside" | "sellside" | "both";

export function normalizeStrategyRdPoolPolarity(
  raw: string | null | undefined
): StrategyRdPoolPolarity | null {
  const token = (raw || "").trim().toLowerCase();
  if (!token) return null;
  if (token === "buyside" || token === "buy_side" || token === "buy-side") return "buyside";
  if (token === "sellside" || token === "sell_side" || token === "sell-side") {
    return "sellside";
  }
  if (token === "both" || token === "either" || token === "any") return "both";
  return null;
}

/**
 * When Pattern polarity (bull/bear) changes, default Trade side to match.
 * Does not apply to Level polarity (high/low) — those stay independent in the UI.
 */
export function strategyRdDefaultTradeSideForPolarity(
  polarity: string | null | undefined
): StrategyRdSlotSide | null {
  const token = (polarity || "").trim().toLowerCase();
  // Never invent High→Long / Low→Short in the composer — BE may default omitted side.
  if (token === "high" || token === "low" || token === "buyside" || token === "sellside") {
    return null;
  }
  const normalized = normalizeStrategyRdPolarity(polarity);
  if (normalized === "bull") return "long";
  if (normalized === "bear") return "short";
  if (normalized === "both") return "both";
  return null;
}

export type StrategyRdSideByPolarity = {
  bull: StrategyRdSlotSide;
  bear: StrategyRdSlotSide;
};

export type StrategyRdSideByLocation = {
  above: StrategyRdSlotSide;
  below: StrategyRdSlotSide;
};

export type StrategyRdSideByLevel = {
  high: StrategyRdSlotSide;
  low: StrategyRdSlotSide;
};

export type StrategyRdSideByPool = {
  buyside: StrategyRdSlotSide;
  sellside: StrategyRdSlotSide;
};

/** Default when Pattern = Both: bullish→long, bearish→short. */
export function strategyRdDefaultSideByPolarity(): StrategyRdSideByPolarity {
  return { bull: "long", bear: "short" };
}

/** Invert when Pattern = Both: bullish→short, bearish→long. */
export function strategyRdInvertSideByPolarity(): StrategyRdSideByPolarity {
  return { bull: "short", bear: "long" };
}

/** Default when Location = Both: above→long, below→short. */
export function strategyRdDefaultSideByLocation(): StrategyRdSideByLocation {
  return { above: "long", below: "short" };
}

/** Default when Level = Both: high→long, low→short. */
export function strategyRdDefaultSideByLevel(): StrategyRdSideByLevel {
  return { high: "long", low: "short" };
}

/** Invert when Level = Both: high→short, low→long (fade / rejection map). */
export function strategyRdInvertSideByLevel(): StrategyRdSideByLevel {
  return { high: "short", low: "long" };
}

/** Default when Pool = Both: buyside→long, sellside→short. */
export function strategyRdDefaultSideByPool(): StrategyRdSideByPool {
  return { buyside: "long", sellside: "short" };
}

/** Invert when Pool = Both: buyside→short, sellside→long. */
export function strategyRdInvertSideByPool(): StrategyRdSideByPool {
  return { buyside: "short", sellside: "long" };
}

export function strategyRdIsInvertSideByLevel(map: StrategyRdSideByLevel): boolean {
  return map.high === "short" && map.low === "long";
}

export function strategyRdIsInvertSideByPool(map: StrategyRdSideByPool): boolean {
  return map.buyside === "short" && map.sellside === "long";
}

export function strategyRdIsInvertSideByPolarity(map: StrategyRdSideByPolarity): boolean {
  return map.bull === "short" && map.bear === "long";
}

/** Read per-polarity trade sides from a setup slot (defaults to matched). */
export function strategyRdSideByPolarityFromSetup(
  setup: Pick<StrategyRdComposerSetupSlot, "side" | "side_by_polarity" | "polarity" | "invert" | "polarity_sides"> | null | undefined
): StrategyRdSideByPolarity {
  const fromSlot = setup?.side_by_polarity || setup?.polarity_sides;
  const bull = normalizeStrategyRdSlotSide(fromSlot?.bull ?? null);
  const bear = normalizeStrategyRdSlotSide(fromSlot?.bear ?? null);
  if (bull && bear && (bull === "long" || bull === "short") && (bear === "long" || bear === "short")) {
    return { bull, bear };
  }
  const side = normalizeStrategyRdSlotSide(setup?.side);
  if (side === "invert" || setup?.invert === true) return strategyRdInvertSideByPolarity();
  if (side === "long") return { bull: "long", bear: "long" };
  if (side === "short") return { bull: "short", bear: "short" };
  return strategyRdDefaultSideByPolarity();
}

/** Read per-location trade sides from a confluence slot (defaults to matched). */
export function strategyRdSideByLocationFromSlot(
  slot:
    | Pick<StrategyRdComposerConfluenceSlot, "side" | "side_by_polarity" | "params">
    | null
    | undefined
): StrategyRdSideByLocation {
  const fromSlot = slot?.side_by_polarity;
  const above = normalizeStrategyRdSlotSide(fromSlot?.above ?? null);
  const below = normalizeStrategyRdSlotSide(fromSlot?.below ?? null);
  if (
    above &&
    below &&
    (above === "long" || above === "short") &&
    (below === "long" || below === "short")
  ) {
    return { above, below };
  }
  const side = normalizeStrategyRdSlotSide(slot?.side);
  if (side === "long") return { above: "long", below: "long" };
  if (side === "short") return { above: "short", below: "short" };
  return strategyRdDefaultSideByLocation();
}

/** Read per-level trade sides when Level = Both (defaults high→long, low→short). */
export function strategyRdSideByLevelFromSetup(
  setup: Pick<StrategyRdComposerSetupSlot, "side" | "side_by_polarity" | "polarity" | "invert" | "polarity_sides"> | null | undefined
): StrategyRdSideByLevel {
  const fromSlot = setup?.side_by_polarity || setup?.polarity_sides;
  const high = normalizeStrategyRdSlotSide(fromSlot?.high ?? null);
  const low = normalizeStrategyRdSlotSide(fromSlot?.low ?? null);
  if (
    high &&
    low &&
    (high === "long" || high === "short") &&
    (low === "long" || low === "short")
  ) {
    return { high, low };
  }
  const side = normalizeStrategyRdSlotSide(setup?.side);
  if (side === "invert" || setup?.invert === true) return strategyRdInvertSideByLevel();
  if (side === "long") return { high: "long", low: "long" };
  if (side === "short") return { high: "short", low: "short" };
  return strategyRdDefaultSideByLevel();
}

/** Read per-pool trade sides when Pool = Both. */
export function strategyRdSideByPoolFromSetup(
  setup: Pick<StrategyRdComposerSetupSlot, "side" | "side_by_polarity" | "polarity" | "invert" | "polarity_sides"> | null | undefined
): StrategyRdSideByPool {
  const fromSlot = setup?.side_by_polarity || setup?.polarity_sides;
  const buyside = normalizeStrategyRdSlotSide(fromSlot?.buyside ?? fromSlot?.high ?? null);
  const sellside = normalizeStrategyRdSlotSide(fromSlot?.sellside ?? fromSlot?.low ?? null);
  if (
    buyside &&
    sellside &&
    (buyside === "long" || buyside === "short") &&
    (sellside === "long" || sellside === "short")
  ) {
    return { buyside, sellside };
  }
  const side = normalizeStrategyRdSlotSide(setup?.side);
  if (side === "invert" || setup?.invert === true) return strategyRdInvertSideByPool();
  if (side === "long") return { buyside: "long", sellside: "long" };
  if (side === "short") return { buyside: "short", sellside: "short" };
  return strategyRdDefaultSideByPool();
}

/**
 * Collapse per-polarity trade sides into the legacy single `side` field when possible.
 * - bull long + bear short → both (matched)
 * - bull long + bear long → long
 * - bull short + bear short → short
 * - crossed invert → invert
 */
export function strategyRdCollapseSideByPolarity(
  map:
    | StrategyRdSideByPolarity
    | StrategyRdSideByLocation
    | StrategyRdSideByLevel
    | StrategyRdSideByPool
): StrategyRdSlotSide {
  if ("buyside" in map && "sellside" in map) {
    if (map.buyside === "long" && map.sellside === "long") return "long";
    if (map.buyside === "short" && map.sellside === "short") return "short";
    if (strategyRdIsInvertSideByPool(map)) return "invert";
    return "both";
  }
  if ("high" in map && "low" in map) {
    if (map.high === "long" && map.low === "long") return "long";
    if (map.high === "short" && map.low === "short") return "short";
    if (strategyRdIsInvertSideByLevel(map)) return "invert";
    return "both";
  }
  if ("bull" in map) {
    if (map.bull === "long" && map.bear === "long") return "long";
    if (map.bull === "short" && map.bear === "short") return "short";
    if (strategyRdIsInvertSideByPolarity(map)) return "invert";
    return "both";
  }
  if (map.above === "long" && map.below === "long") return "long";
  if (map.above === "short" && map.below === "short") return "short";
  return "both";
}

export function strategyRdIsCrossedSideByPolarity(map: StrategyRdSideByPolarity): boolean {
  return map.bull === "short" && map.bear === "long";
}

export function strategyRdConceptDirectionMode(
  concept: Pick<StrategyRdConcept, "direction_mode" | "agnostic"> | null | undefined
): StrategyRdDirectionMode {
  const mode = (concept?.direction_mode || "").trim().toLowerCase();
  if (
    mode === "implied_long" ||
    mode === "implied_short" ||
    mode === "side_required" ||
    mode === "none"
  ) {
    return mode;
  }
  if (concept?.agnostic === true) return "side_required";
  return "none";
}

export function strategyRdConceptNeedsSide(
  concept: Pick<StrategyRdConcept, "direction_mode" | "agnostic"> | null | undefined
): boolean {
  return strategyRdConceptDirectionMode(concept) === "side_required";
}

/**
 * Neutral + directional setups may use Both (Both = OR polarities for families like engulf/fvg).
 * Prefer catalog settings.enum_values when present.
 */
export function strategyRdSetupAllowsBothSide(
  concept: Pick<StrategyRdConcept, "direction_mode" | "agnostic" | "settings"> | null | undefined
): boolean {
  if (!concept) return true;
  const settings = Array.isArray(concept.settings) ? concept.settings : [];
  if (settings.length > 0) {
    const sideSetting = settings.find((s) => s.key === "side" && !s.param);
    if (sideSetting?.enum_values?.length) {
      return sideSetting.enum_values.some(
        (row) => normalizeStrategyRdSlotSide(String(row.value)) === "both"
      );
    }
  }
  return true;
}

/** Family / agnostic confluences expose Long · Short · Both (or catalog enum). */
export function strategyRdConfluenceAllowsBothSide(
  _concept: Pick<
    StrategyRdConcept,
    "direction_mode" | "agnostic" | "pair_group" | "branch_hint" | "settings"
  > | null | undefined
): boolean {
  return true;
}

/**
 * Trade side named by a concrete catalog id (`fvg_bull`, `regime_trend_up`, `above_sma50`).
 * `legacyKey` is the catalog `legacy_ids` key (`long` / `short`) when the id itself has no token.
 * Level words (high/low) are not a trade side. Null means the id is both-sided or neutral.
 */
export function strategyRdImpliedTradeSide(
  id: string | null | undefined,
  legacyKey?: string | null
): "long" | "short" | null {
  const key = (legacyKey || "").trim().toLowerCase();
  if (key === "long" || key === "short") return key;
  const raw = (id || "").trim().toLowerCase();
  if (!raw) return null;
  if (/(^|_)bull(ish)?($|_)/.test(raw) || raw.includes("bullish")) return "long";
  if (/(^|_)bear(ish)?($|_)/.test(raw) || raw.includes("bearish")) return "short";
  if (/(^|_)above($|_)/.test(raw)) return "long";
  if (/(^|_)below($|_)/.test(raw)) return "short";
  if (/(^|_)up($|_)/.test(raw)) return "long";
  if (/(^|_)down($|_)/.test(raw)) return "short";
  return null;
}

export function strategyRdDefaultSetupSide(
  concept:
    | (Pick<StrategyRdConcept, "direction_mode" | "agnostic" | "settings"> & { id?: string })
    | null
    | undefined,
  sourceId?: string | null
): StrategyRdSlotSide {
  const implied = strategyRdImpliedTradeSide(sourceId || concept?.id);
  if (implied) return implied;
  const settings = concept?.settings;
  const list = Array.isArray(settings) ? settings : [];
  if (list.length > 0) {
    const sideSetting = list.find((s) => s.key === "side" && !s.param);
    const fromDefault = normalizeStrategyRdSlotSide(
      sideSetting?.default == null ? null : String(sideSetting.default)
    );
    if (fromDefault) return fromDefault;
  }
  return strategyRdSetupAllowsBothSide(concept) ? "both" : "long";
}

export function strategyRdDefaultConfluenceSide(
  concept:
    | (Pick<
        StrategyRdConcept,
        "direction_mode" | "agnostic" | "pair_group" | "branch_hint" | "settings"
      > & { id?: string })
    | null
    | undefined,
  sourceId?: string | null
): StrategyRdSlotSide {
  const implied = strategyRdImpliedTradeSide(sourceId || concept?.id);
  if (implied) return implied;
  const settings = concept?.settings;
  const list = Array.isArray(settings) ? settings : [];
  if (list.length > 0) {
    const sideSetting = list.find((s) => s.key === "side" && !s.param);
    const fromDefault = normalizeStrategyRdSlotSide(
      sideSetting?.default == null ? null : String(sideSetting.default)
    );
    if (fromDefault) return fromDefault;
  }
  return "both";
}

export function strategyRdSetupSideOptions(
  concept: Pick<StrategyRdConcept, "direction_mode" | "agnostic" | "settings"> | null | undefined
): StrategyRdSlotSide[] {
  const settings = concept?.settings;
  const list = Array.isArray(settings) ? settings : [];
  if (list.length > 0) {
    const sideSetting = list.find((s) => s.key === "side" && !s.param);
    const fromEnum = (sideSetting?.enum_values || [])
      .map((row) => normalizeStrategyRdSlotSide(String(row.value)))
      .filter((v): v is StrategyRdSlotSide => v != null);
    if (fromEnum.length > 0) return fromEnum;
  }
  return strategyRdSetupAllowsBothSide(concept)
    ? ["long", "short", "both"]
    : ["long", "short"];
}

export function strategyRdConfluenceSideOptions(
  concept: Pick<
    StrategyRdConcept,
    "direction_mode" | "agnostic" | "pair_group" | "branch_hint" | "settings"
  > | null | undefined
): StrategyRdSlotSide[] {
  const settings = concept?.settings;
  const list = Array.isArray(settings) ? settings : [];
  if (list.length > 0) {
    const sideSetting = list.find((s) => s.key === "side" && !s.param);
    const fromEnum = (sideSetting?.enum_values || [])
      .map((row) => normalizeStrategyRdSlotSide(String(row.value)))
      .filter((v): v is StrategyRdSlotSide => v != null);
    if (fromEnum.length > 0) return fromEnum;
  }
  return ["long", "short", "both"];
}

/**
 * Chip copy for how Both expands — prefer BE `branch_hint.label`.
 * Otherwise "Long: {name} · Short: {name}" from branch ids. Never invent entry polarity.
 */
export function strategyRdBranchHintLabel(
  concept: Pick<StrategyRdConcept, "branch_hint" | "name"> | null | undefined,
  labelForId?: (id: string) => string
): string | null {
  const hint = concept?.branch_hint;
  if (!hint) return null;
  const beLabel = (hint.label || "").trim();
  if (beLabel) return beLabel;
  const nameOf = labelForId || ((id: string) => id.replace(/_/g, " "));
  const longId = (hint.long || "").trim();
  const shortId = (hint.short || "").trim();
  const parts: string[] = [];
  if (longId) parts.push(`Long: ${nameOf(longId)}`);
  if (shortId) parts.push(`Short: ${nameOf(shortId)}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Source label for instrument picker / collapsed Step 1. */
export function strategyRdInstrumentSourceLabel(
  instrument: Pick<
    StrategyRdInstrument,
    "source_hidden" | "market_data_broker_display" | "market_data_broker" | "md_source"
  > | null | undefined
): string {
  if (!instrument) return "";
  if (instrument.source_hidden) {
    return (
      instrument.market_data_broker_display?.trim() ||
      "US market data"
    );
  }
  return (
    instrument.market_data_broker_display?.trim() ||
    instrument.market_data_broker?.trim() ||
    instrument.md_source?.trim() ||
    ""
  );
}

/** Broker/source pins for resolve/scan — omit when Databento is hidden. */
export function strategyRdInstrumentBrokerPins(
  instrument: Pick<
    StrategyRdInstrument,
    "source_hidden" | "market_data_broker" | "md_source"
  > | null | undefined
): { market_data_broker?: string; md_source?: string } {
  if (!instrument || instrument.source_hidden) return {};
  const broker = instrument.market_data_broker?.trim();
  const source = instrument.md_source?.trim();
  return {
    ...(broker ? { market_data_broker: broker } : {}),
    ...(source ? { md_source: source } : {}),
  };
}

export function strategyRdConfluenceIds(
  confluences: Array<string | StrategyRdComposerConfluenceSlot> | null | undefined
): string[] {
  if (!Array.isArray(confluences)) return [];
  const out: string[] = [];
  for (const item of confluences) {
    if (typeof item === "string") {
      const id = item.trim();
      if (id) out.push(id);
      continue;
    }
    if (item && typeof item === "object") {
      const id = String(item.id || "").trim();
      if (id) out.push(id);
    }
  }
  return out;
}

/** Resolve slot TF from candle_type (preferred) or aliases tf / timeframe. */
export function strategyRdSlotCandleType(
  slot:
    | {
        candle_type?: string | null;
        tf?: string | null;
        timeframe?: string | null;
      }
    | null
    | undefined,
  fallback?: string | null
): string {
  return (
    (slot?.candle_type || "").trim() ||
    (slot?.tf || "").trim() ||
    (slot?.timeframe || "").trim() ||
    (fallback || "").trim()
  );
}

/**
 * Normalize confluence slots for preview/scan POST bodies.
 * Always objects with explicit `candle_type` — never a flat string[].
 */
export function strategyRdConfluenceSlotsForRequest(
  confluences: Array<string | StrategyRdComposerConfluenceSlot> | null | undefined,
  setupCandleType: string
): StrategyRdComposerConfluenceSlot[] {
  if (!Array.isArray(confluences) || confluences.length === 0) return [];
  const setupTf = setupCandleType.trim();
  const out: StrategyRdComposerConfluenceSlot[] = [];
  for (const item of confluences) {
    if (typeof item === "string") {
      const id = item.trim();
      if (!id) continue;
      out.push({
        id,
        side: "both",
        ...(setupTf ? { candle_type: setupTf } : {}),
      });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const id = String(item.id || "").trim();
    if (!id) continue;
    const side = normalizeStrategyRdSlotSide(item.side) || "both";
    const polarity =
      normalizeStrategyRdPolarity(
        item.polarity != null ? String(item.polarity) : null
      ) ||
      (item.polarity != null && String(item.polarity).trim()
        ? String(item.polarity).trim()
        : null);
    const candleType = strategyRdSlotCandleType(item, setupTf);
    const rawParams =
      item.params && typeof item.params === "object" && !Array.isArray(item.params)
        ? item.params
        : item.settings && typeof item.settings === "object" && !Array.isArray(item.settings)
          ? item.settings
          : null;
    // BE stamps Entry → Max wait (and the setup clock) onto location slots on every
    // normalize — never echo them back.
    const params =
      rawParams && ("entry_max_wait_hours" in rawParams || "setup_minutes" in rawParams)
        ? Object.fromEntries(
            Object.entries(rawParams).filter(
              ([key]) => key !== "entry_max_wait_hours" && key !== "setup_minutes"
            )
          )
        : rawParams;
    const location =
      params && typeof params.polarity !== "undefined"
        ? String(params.polarity).trim().toLowerCase()
        : "";
    const sideByLocation =
      location === "both" ? strategyRdSideByLocationFromSlot(item) : null;
    const resolvedSide = sideByLocation
      ? strategyRdCollapseSideByPolarity(sideByLocation)
      : side;
    out.push({
      id,
      side: resolvedSide,
      ...(polarity ? { polarity } : {}),
      ...(sideByLocation
        ? {
            side_by_polarity: {
              above: sideByLocation.above,
              below: sideByLocation.below,
            },
          }
        : {}),
      ...(candleType ? { candle_type: candleType } : {}),
      ...(params && Object.keys(params).length > 0 ? { params } : {}),
      ...(item.optional === true ? { optional: true } : {}),
    });
  }
  return out;
}

/**
 * BE slot keys for the composer's confluences, in order: the first slot of a concept keeps
 * its id, repeats get `#2`, `#3`… (`side_gate.assign_slot_keys`).
 */
export function strategyRdConfluenceSlotKeys(
  confluences: ReadonlyArray<Pick<StrategyRdComposerConfluenceSlot, "id">>
): string[] {
  const seen = new Map<string, number>();
  return confluences.map((slot) => {
    const id = String(slot.id || "").trim();
    const n = (seen.get(id) ?? 0) + 1;
    seen.set(id, n);
    return n === 1 ? id : `${id}#${n}`;
  });
}

/** N of M: `optional_min` for the wire — only when some confluence is optional; capped at M. */
export function strategyRdOptionalMinForWire(
  confluences: ReadonlyArray<Pick<StrategyRdComposerConfluenceSlot, "optional">>,
  optionalMin: number | null | undefined
): number | undefined {
  const m = confluences.filter((c) => c.optional === true).length;
  if (m === 0) return undefined;
  const n = Math.floor(Number(optionalMin) || 1);
  return Math.max(1, Math.min(n, m));
}

/** Confluence ids whose slot TF matches the chart (omit higher/lower TFs from drawings). */
export function strategyRdConfluenceIdsForChartTf(
  slots: readonly StrategyRdComposerConfluenceSlot[],
  chartCandleType: string,
  setupCandleType: string
): string[] {
  const chartTf = chartCandleType.trim();
  if (!chartTf) return slots.map((s) => s.id.trim()).filter(Boolean);
  return slots
    .filter((s) => {
      const eff = (s.candle_type || setupCandleType).trim();
      return eff === chartTf;
    })
    .map((s) => s.id.trim())
    .filter(Boolean);
}

export function parseStrategyRdLogicPath(raw: unknown): StrategyRdLogicPath | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const asLines = (value: unknown): string[] =>
    Array.isArray(value)
      ? value.map((v) => (typeof v === "string" ? v : v == null ? "" : String(v))).filter(Boolean)
      : [];
  const text = typeof row.text === "string" ? row.text : "";
  const notes = asLines(row.notes);
  const summary = asLines(row.summary);
  const long = asLines(row.long);
  const short = asLines(row.short);
  if (
    !text &&
    notes.length === 0 &&
    summary.length === 0 &&
    long.length === 0 &&
    short.length === 0
  ) {
    return null;
  }
  return { notes, summary, long, short, text };
}

export function strategyRdNestedComposerFingerprint(input: {
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  sessionIds: string[];
  srScopes?: string[] | null;
  setupCandleType: string;
  /** N of M, and the custom entry rule / filter — they change which setups are hits. */
  optionalMin?: number | null;
  entryRule?: unknown;
  entryFilter?: unknown;
}): string {
  return JSON.stringify({
    ...(input.optionalMin != null ? { optionalMin: input.optionalMin } : {}),
    ...(input.entryRule ? { entryRule: input.entryRule } : {}),
    ...(input.entryFilter ? { entryFilter: input.entryFilter } : {}),
    setup: {
      id: input.setup.id,
      side: normalizeStrategyRdSlotSide(input.setup.side) || null,
      polarity:
        normalizeStrategyRdPolarity(
          input.setup.polarity != null ? String(input.setup.polarity) : null
        ) ||
        (input.setup.polarity != null ? String(input.setup.polarity).trim() || null : null),
      side_by_polarity: input.setup.side_by_polarity || null,
      params: input.setup.params || null,
    },
    setupCandleType: input.setupCandleType,
    confluences: input.confluences.map((c) => ({
      id: c.id,
      side: normalizeStrategyRdSlotSide(c.side) || null,
      polarity:
        normalizeStrategyRdPolarity(c.polarity != null ? String(c.polarity) : null) ||
        (c.polarity != null ? String(c.polarity).trim() || null : null),
      side_by_polarity: c.side_by_polarity || null,
      candle_type: (c.candle_type || "").trim() || null,
      params: c.params || c.settings || null,
      ...(c.optional === true ? { optional: true } : {}),
    })),
    entry: {
      mode: input.entry.mode,
      side: normalizeStrategyRdSlotSide(input.entry.side) || null,
      candle_type: (input.entry.candle_type || "").trim() || null,
      max_wait: strategyRdMaxWaitForWire(input.entry.max_wait) ?? null,
    },
    sessionIds: [...input.sessionIds].map((s) => s.trim().toLowerCase()).filter(Boolean).sort(),
    srScopes: [...(input.srScopes || [])].map((s) => s.trim().toLowerCase()).filter(Boolean).sort(),
  });
}

/** Compact nested composer fields for preview/scan bodies. */
export function compactNestedComposerFields(input: {
  setup?: StrategyRdComposerSetupSlot | null;
  confluences?: Array<string | StrategyRdComposerConfluenceSlot> | null;
  entry?: StrategyRdComposerEntrySlot | null;
  setupCandleType?: string | null;
}): {
  setup?: StrategyRdComposerSetupSlot;
  setup_id?: string;
  pattern_id?: string;
  confluences?: StrategyRdComposerConfluenceSlot[];
  entry?: StrategyRdComposerEntrySlot;
  entry_mode?: string;
} {
  const out: {
    setup?: StrategyRdComposerSetupSlot;
    setup_id?: string;
    pattern_id?: string;
    confluences?: StrategyRdComposerConfluenceSlot[];
    entry?: StrategyRdComposerEntrySlot;
    entry_mode?: string;
  } = {};

  const setupId = (input.setup?.id || "").trim();
  if (setupId) {
    const polarityRaw =
      input.setup?.polarity != null ? String(input.setup.polarity).trim() : "";
    const polarityKind = setupPolarityKindForId(setupId);
    const patternPolWhenUnknown =
      polarityKind === "none" ? normalizeStrategyRdPolarity(polarityRaw) : null;
    const levelPol =
      polarityKind === "level" ? normalizeStrategyRdLevelPolarity(polarityRaw) : null;
    const poolPol =
      polarityKind === "pool" ? normalizeStrategyRdPoolPolarity(polarityRaw) : null;
    const patternPol =
      polarityKind === "pattern" || (polarityKind === "none" && patternPolWhenUnknown)
        ? normalizeStrategyRdPolarity(polarityRaw)
        : null;
    const polarity =
      levelPol ||
      poolPol ||
      patternPol ||
      (polarityRaw ? polarityRaw : null) ||
      // Level / Pattern / Pool setups must always send polarity (BE defaults wrongly if omitted).
      (polarityKind === "level" || polarityKind === "pattern" || polarityKind === "pool"
        ? "both"
        : null);
    const sideByLevel =
      polarityKind === "level" && levelPol === "both"
        ? strategyRdSideByLevelFromSetup(input.setup)
        : null;
    const sideByPool =
      polarityKind === "pool" && poolPol === "both"
        ? strategyRdSideByPoolFromSetup(input.setup)
        : null;
    const sideByPolarity =
      patternPol === "both" &&
      (polarityKind === "pattern" || polarityKind === "none")
        ? strategyRdSideByPolarityFromSetup(input.setup)
        : null;
    const sideFromMap = sideByLevel
      ? strategyRdCollapseSideByPolarity(sideByLevel)
      : sideByPool
        ? strategyRdCollapseSideByPolarity(sideByPool)
        : sideByPolarity
          ? strategyRdCollapseSideByPolarity(sideByPolarity)
          : null;
    const side =
      sideFromMap ||
      normalizeStrategyRdSlotSide(input.setup?.side) ||
      (input.setup?.invert === true ? "invert" : null);
    const isInvert =
      side === "invert" ||
      input.setup?.invert === true ||
      (sideByLevel != null && strategyRdIsInvertSideByLevel(sideByLevel)) ||
      (sideByPool != null && strategyRdIsInvertSideByPool(sideByPool)) ||
      (sideByPolarity != null && strategyRdIsInvertSideByPolarity(sideByPolarity));
    const polaritySides = sideByLevel
      ? { high: sideByLevel.high, low: sideByLevel.low }
      : sideByPool
        ? { buyside: sideByPool.buyside, sellside: sideByPool.sellside }
        : sideByPolarity
          ? { bull: sideByPolarity.bull, bear: sideByPolarity.bear }
          : null;
    const params =
      input.setup?.params &&
      typeof input.setup.params === "object" &&
      !Array.isArray(input.setup.params)
        ? input.setup.params
        : null;
    const candleType =
      (input.setup?.candle_type || "").trim() ||
      (input.setupCandleType || "").trim() ||
      "";
    // Polarity both: always send an explicit remap so BE is not treated as neutral.
    // Invert → side:"invert" (+ invert:true + polarity_sides). Matched → side:"both" + polarity_sides.
    const wireSide = isInvert ? "invert" : side;
    out.setup = {
      id: setupId,
      ...(wireSide ? { side: wireSide } : {}),
      ...(polarity ? { polarity } : {}),
      // Level setups: setup.level ≡ setup.polarity (BE alias).
      ...(levelPol && polarity ? { level: polarity } : {}),
      ...(isInvert ? { invert: true } : {}),
      ...(polaritySides
        ? {
            side_by_polarity: polaritySides,
            polarity_sides: polaritySides,
          }
        : {}),
      ...(params && Object.keys(params).length > 0 ? { params } : {}),
      // Level / Pattern / Pool: always send candle_type with polarity (page TF).
      ...(candleType &&
      (polarityKind === "level" || polarityKind === "pattern" || polarityKind === "pool")
        ? { candle_type: candleType }
        : {}),
    };
    out.setup_id = setupId;
    out.pattern_id = setupId;
  }

  if (Array.isArray(input.confluences) && input.confluences.length > 0) {
    const slots = strategyRdConfluenceSlotsForRequest(
      input.confluences,
      (input.setupCandleType || "").trim()
    );
    if (slots.length > 0) out.confluences = slots;
  }

  const entryMode = (input.entry?.mode || "").trim();
  if (entryMode) {
    const side = normalizeStrategyRdSlotSide(input.entry?.side);
    const candleType =
      strategyRdSlotCandleType(input.entry, null) ||
      (input.setupCandleType || "").trim() ||
      null;
    out.entry = strategyRdEntryWithMaxWait(
      {
        mode: entryMode,
        ...(side ? { side } : {}),
        ...(candleType ? { candle_type: candleType } : {}),
      },
      input.entry
    );
    out.entry_mode = entryMode;
  }

  return out;
}

export type StrategyRdComposerSummaryColumn = {
  title: string;
  rows: Array<{ label: string; value: string }>;
};

function strategyRdSideLabel(side: string | null | undefined): string {
  const normalized = normalizeStrategyRdSlotSide(side);
  if (normalized === "long") return "Long";
  if (normalized === "short") return "Short";
  if (normalized === "both") return "Both";
  if (normalized === "invert") return "Invert";
  return (side || "").trim();
}

function strategyRdPolarityLabel(polarity: string | null | undefined): string {
  const pattern = normalizeStrategyRdPolarity(polarity);
  if (pattern === "bull") return "Bullish";
  if (pattern === "bear") return "Bearish";
  if (pattern === "both") return "Both";
  const level = normalizeStrategyRdLevelPolarity(polarity);
  if (level === "high") return "High";
  if (level === "low") return "Low";
  if (level === "both") return "Both";
  const pool = normalizeStrategyRdPoolPolarity(polarity);
  if (pool === "buyside") return "Buyside";
  if (pool === "sellside") return "Sellside";
  if (pool === "both") return "Both";
  const raw = (polarity || "").trim();
  if (!raw) return "";
  if (raw === "above") return "Above";
  if (raw === "below") return "Below";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** @deprecated Pattern polarity and trade side are independent — use selection reading. */
export function strategyRdSideBiasWord(
  side: string | null | undefined
): "bullish" | "bearish" | "either" | "" {
  const normalized = normalizeStrategyRdSlotSide(side);
  if (normalized === "long") return "bullish";
  if (normalized === "short") return "bearish";
  if (normalized === "both") return "either";
  return "";
}

/** Plain trade-side labels (not pattern polarity). */
export function strategyRdSideSelectLabel(
  side: StrategyRdSlotSide,
  opts?: { polarityKind?: "pattern" | "level" | "pool" | "none" }
): string {
  if (side === "long") return "Long";
  if (side === "short") return "Short";
  const kind = opts?.polarityKind;
  if (side === "invert") {
    if (kind === "pattern") return "Bull Short · Bear Long";
    if (kind === "pool") return "Buyside Short · Sellside Long";
    if (kind === "level") return "High Short · Low Long";
    return "Invert";
  }
  if (side === "both") {
    // Matched polarity → trade map (mirror of Invert). Keep "Both" for neutrals.
    if (kind === "pattern") return "Bull Long · Bear Short";
    if (kind === "pool") return "Buyside Long · Sellside Short";
    if (kind === "level") return "High Long · Low Short";
    return "Both";
  }
  return "Both";
}

/**
 * Plain-language reading: pattern/level polarity + concept, then trade side.
 * They can disagree — e.g. "Bearish fair value gap · trade Long" or "High · trade Short".
 */
export function strategyRdConceptSideReading(input: {
  conceptName?: string | null;
  conceptId?: string | null;
  side?: string | null;
  polarity?: string | null;
  /** When true, polarity is Level (high/low/both), not Pattern (bull/bear). */
  levelPolarity?: boolean;
  /** When true, polarity is Pool (buyside/sellside/both). */
  poolPolarity?: boolean;
  /** MA location from params.polarity (above/below/both). */
  locationPolarity?: string | null;
}): string {
  const rawName =
    (input.conceptName || "").trim() ||
    (input.conceptId || "").trim().replace(/_/g, " ") ||
    "pattern";
  const name =
    rawName.length > 0
      ? rawName.charAt(0).toLowerCase() + rawName.slice(1)
      : rawName;

  const location = (input.locationPolarity || "").trim().toLowerCase();
  let patternPart = rawName;
  if (input.poolPolarity) {
    const pool = normalizeStrategyRdPoolPolarity(input.polarity);
    if (pool === "buyside") patternPart = `Buyside ${name}`;
    else if (pool === "sellside") patternPart = `Sellside ${name}`;
    else if (pool === "both") patternPart = `${rawName} (buyside or sellside)`;
  } else if (input.levelPolarity) {
    const level = normalizeStrategyRdLevelPolarity(input.polarity);
    if (level === "high") patternPart = `High ${name}`;
    else if (level === "low") patternPart = `Low ${name}`;
    else if (level === "both") patternPart = `${rawName} (high or low)`;
  } else {
    const patternPolarity = normalizeStrategyRdPolarity(input.polarity);
    if (patternPolarity === "bull") patternPart = `Bullish ${name}`;
    else if (patternPolarity === "bear") patternPart = `Bearish ${name}`;
    else if (patternPolarity === "both") patternPart = `${rawName} (bullish or bearish)`;
    else if (location === "above") patternPart = `Above ${rawName}`;
    else if (location === "below") patternPart = `Below ${rawName}`;
    else if (location === "both") patternPart = `${rawName} (above or below)`;
  }

  const trade = normalizeStrategyRdSlotSide(input.side);
  if (!trade) return patternPart;
  if (trade === "both") return `${patternPart} · either trade side`;
  if (trade === "invert") {
    if (input.poolPolarity) return `${patternPart} · Buyside Short · Sellside Long`;
    if (input.levelPolarity) return `${patternPart} · High Short · Low Long`;
    return `${patternPart} · Bull Short · Bear Long`;
  }
  return `${patternPart} · trade ${trade === "long" ? "Long" : "Short"}`;
}

/** Column summary for the recipe “Your selections” strip. */
export function strategyRdComposerSummaryColumns(input: {
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  setupCandleType: string;
  sessionIds?: string[] | null;
  /** Catalog polarity control — Pattern, Level, Pool, or none (trade side only). */
  setupPolarityKind?: "pattern" | "level" | "pool" | "none";
  labelForConcept?: (id: string) => string;
  labelForEntry?: (mode: string) => string;
  labelForSession?: (id: string) => string;
  /** Catalog settings for a concept — turns params into "Scope Day" instead of "scope day". */
  settingsForConcept?: (id: string) => readonly ComposerParamSetting[] | null | undefined;
}): StrategyRdComposerSummaryColumn[] {
  const conceptLabel = input.labelForConcept || ((id: string) => id);
  const entryLabel = input.labelForEntry || ((mode: string) => mode);
  const sessionLabel = input.labelForSession || ((id: string) => id);

  const setupName = conceptLabel(input.setup.id) || "N/A";
  const setupPolarityRaw =
    input.setup.polarity != null ? String(input.setup.polarity).trim() : "";
  const setupPolarity = strategyRdPolarityLabel(setupPolarityRaw);
  const kind = input.setupPolarityKind || "none";
  const levelPol = normalizeStrategyRdLevelPolarity(setupPolarityRaw);
  const poolPol = normalizeStrategyRdPoolPolarity(setupPolarityRaw);
  const sideByLevel = strategyRdSideByLevelFromSetup(input.setup);
  const sideByPool = strategyRdSideByPoolFromSetup(input.setup);
  const sideBy = strategyRdSideByPolarityFromSetup(input.setup);
  const polarityNorm = normalizeStrategyRdPolarity(setupPolarityRaw);
  const setupSide =
    kind === "level" && levelPol === "both"
      ? strategyRdIsInvertSideByLevel(sideByLevel)
        ? "Invert (High → Short · Low → Long)"
        : `High ${strategyRdSideLabel(sideByLevel.high)} · Low ${strategyRdSideLabel(sideByLevel.low)}`
      : kind === "pool" && poolPol === "both"
        ? strategyRdIsInvertSideByPool(sideByPool)
          ? "Invert (Buyside → Short · Sellside → Long)"
          : `Buyside ${strategyRdSideLabel(sideByPool.buyside)} · Sellside ${strategyRdSideLabel(sideByPool.sellside)}`
        : kind === "pattern" && polarityNorm === "both"
          ? strategyRdIsInvertSideByPolarity(sideBy)
            ? "Invert (Bull → Short · Bear → Long)"
            : `Bull ${strategyRdSideLabel(sideBy.bull)} · Bear ${strategyRdSideLabel(sideBy.bear)}`
          : strategyRdSideLabel(input.setup.side) || "N/A";
  const sessions = (input.sessionIds || []).map((s) => s.trim()).filter(Boolean);
  const setupRows: Array<{ label: string; value: string }> = [
    { label: "Setup", value: setupName },
  ];
  if (setupPolarity) {
    const isPoolSetup = POOL_SETUP_IDS.has((input.setup.id || "").trim());
    const polLabel = isPoolSetup
      ? "Pool"
      : kind === "level"
        ? "Level"
        : kind === "pool"
          ? "Pool"
          : kind === "pattern"
            ? "Pattern"
            : "Polarity";
    const polValue =
      isPoolSetup && setupPolarity === "High"
        ? "High (buyside)"
        : isPoolSetup && setupPolarity === "Low"
          ? "Low (sellside)"
          : setupPolarity;
    setupRows.push({
      label: polLabel,
      value: polValue,
    });
  }
  setupRows.push(
    { label: "Trade side", value: setupSide },
    {
      label: "TF",
      value: input.setupCandleType.trim() || "N/A",
    }
  );
  const setupSwingLength = input.setup.params?.swing_length;
  if (setupSwingLength != null && setupSwingLength !== "") {
    const swingLabel = strategyRdSwingLengthCatalogLabel(setupSwingLength);
    if (swingLabel) {
      setupRows.push({ label: "Swing length", value: swingLabel });
    }
  }
  const setupFast = input.setup.params?.fast;
  const setupSlow = input.setup.params?.slow;
  if (setupFast != null && setupFast !== "") {
    setupRows.push({ label: "Fast length", value: String(setupFast) });
  }
  if (setupSlow != null && setupSlow !== "") {
    setupRows.push({ label: "Slow length", value: String(setupSlow) });
  }
  const setupMaKind = input.setup.params?.ma_kind;
  const setupMaLength = input.setup.params?.ma_length;
  if (
    (setupMaKind != null && setupMaKind !== "") ||
    (setupMaLength != null && setupMaLength !== "")
  ) {
    const kindToken = String(setupMaKind ?? "ema").trim().toLowerCase();
    const kindLabel = kindToken === "sma" ? "SMA" : "EMA";
    const lengthToken =
      setupMaLength != null && setupMaLength !== "" ? String(setupMaLength) : "";
    setupRows.push({
      label: "Pullback MA",
      value: lengthToken ? `${kindLabel} ${lengthToken}` : kindLabel,
    });
  }
  const setupMinPct = input.setup.params?.min_pct;
  if (setupMinPct != null && setupMinPct !== "") {
    const pctLabel = strategyRdPctBreakMinPctLabel(setupMinPct);
    setupRows.push({
      label: "Min % move",
      value: pctLabel || `${setupMinPct}%`,
    });
  }
  const setupVolExpand = input.setup.params?.require_vol_expand;
  if (setupVolExpand === true || setupVolExpand === "true" || setupVolExpand === 1) {
    setupRows.push({ label: "Volume expansion", value: "Required" });
  }
  if (sessions.length > 0) {
    setupRows.push({
      label: "Sessions",
      value: sessions.map(sessionLabel).join(", "),
    });
  }

  const confluenceRows: Array<{ label: string; value: string }> =
    input.confluences.length === 0
      ? [
          { label: "Filter", value: "None" },
          { label: "Trade side", value: "N/A" },
          { label: "TF", value: "N/A" },
        ]
      : input.confluences.length === 1
        ? (() => {
            const c = input.confluences[0]!;
            const locationRaw =
              c.params && typeof c.params.polarity !== "undefined"
                ? String(c.params.polarity).trim().toLowerCase()
                : "";
            const location = locationRaw
              ? strategyRdPolarityLabel(locationRaw)
              : strategyRdPolarityLabel(c.polarity);
            const sideByLoc =
              locationRaw === "both" ? strategyRdSideByLocationFromSlot(c) : null;
            const side = sideByLoc
              ? `Above ${strategyRdSideLabel(sideByLoc.above)} · Below ${strategyRdSideLabel(sideByLoc.below)}`
              : strategyRdSideLabel(c.side) || "N/A";
            const paramBits = formatComposerParamBits(
              c.params && typeof c.params === "object" && !Array.isArray(c.params)
                ? (c.params as Record<string, unknown>)
                : null,
              input.settingsForConcept?.(c.id)
            );
            const rows: Array<{ label: string; value: string }> = [
              {
                label: "Filter",
                value: [conceptLabel(c.id), paramBits].filter(Boolean).join(" · ") || "N/A",
              },
            ];
            if (location) rows.push({ label: "Location", value: location });
            rows.push(
              { label: "Trade side", value: side },
              {
                label: "TF",
                value: (c.candle_type || input.setupCandleType).trim() || "N/A",
              }
            );
            return rows;
          })()
        : input.confluences.map((c, index) => {
            const locationRaw =
              c.params && typeof c.params.polarity !== "undefined"
                ? String(c.params.polarity).trim().toLowerCase()
                : "";
            const sideByLoc =
              locationRaw === "both" ? strategyRdSideByLocationFromSlot(c) : null;
            const side = sideByLoc
              ? `Above ${strategyRdSideLabel(sideByLoc.above)} · Below ${strategyRdSideLabel(sideByLoc.below)}`
              : strategyRdSideLabel(c.side);
            const tf = (c.candle_type || input.setupCandleType).trim();
            const location = locationRaw
              ? strategyRdPolarityLabel(locationRaw)
              : strategyRdPolarityLabel(c.polarity);
            const paramBits = formatComposerParamBits(
              c.params && typeof c.params === "object" && !Array.isArray(c.params)
                ? (c.params as Record<string, unknown>)
                : null,
              input.settingsForConcept?.(c.id)
            );
            return {
              label: `Filter ${index + 1}`,
              value: [conceptLabel(c.id), paramBits, location, side, tf]
                .filter(Boolean)
                .join(" · "),
            };
          });

  const entrySide = strategyRdSideLabel(input.entry.side) || "N/A";
  const entryTf = (input.entry.candle_type || input.setupCandleType).trim();
  const entryRows: Array<{ label: string; value: string }> = [
    { label: "Mode", value: entryLabel(input.entry.mode) || "N/A" },
    { label: "Trade side", value: entrySide },
    { label: "TF", value: entryTf || "N/A" },
  ];

  return [
    { title: "Setup", rows: setupRows },
    { title: "Confluences", rows: confluenceRows },
    { title: "Entry", rows: entryRows },
  ];
}

export function strategyRdComposerCollapsedSummary(input: {
  setup: StrategyRdComposerSetupSlot;
  confluences: StrategyRdComposerConfluenceSlot[];
  entry: StrategyRdComposerEntrySlot;
  setupCandleType: string;
  logicPathSummary?: string[] | null;
  /** Optional human labels (id → display name). */
  labelForConcept?: (id: string) => string;
  labelForEntry?: (mode: string) => string;
  labelForSession?: (id: string) => string;
  sessionIds?: string[] | null;
}): string[] {
  if (input.logicPathSummary && input.logicPathSummary.length > 0) {
    return [...input.logicPathSummary];
  }
  return strategyRdComposerSummaryColumns(input).flatMap((col) =>
    col.rows.map((row) => `${col.title} · ${row.label}: ${row.value}`)
  );
}

function touchJson(value: unknown): string {
  return JSON.stringify(value ?? null);
}

/** Which composer sections a chat apply changed, so the Lab can flash them. */
export function strategyRdComposerTouchKeys(
  prev: {
    setup: StrategyRdComposerSetupSlot;
    confluences: StrategyRdComposerConfluenceSlot[];
    entry: StrategyRdComposerEntrySlot;
    sessionIds?: string[] | null;
    candleType?: string | null;
  },
  next: {
    setup: StrategyRdComposerSetupSlot;
    confluences: StrategyRdComposerConfluenceSlot[];
    entry: StrategyRdComposerEntrySlot;
    sessionIds?: string[] | null;
    candleType?: string | null;
  }
): string[] {
  const keys: string[] = [];
  const setupChanged =
    prev.setup.id !== next.setup.id ||
    String(prev.setup.polarity ?? "") !== String(next.setup.polarity ?? "") ||
    String(prev.setup.side ?? "") !== String(next.setup.side ?? "") ||
    touchJson(prev.setup.params) !== touchJson(next.setup.params) ||
    String(prev.setup.name ?? "") !== String(next.setup.name ?? "");
  if (setupChanged) keys.push("setup");
  if (touchJson(prev.sessionIds || []) !== touchJson(next.sessionIds || [])) {
    keys.push("sessions");
  }
  const max = Math.max(prev.confluences.length, next.confluences.length);
  for (let index = 0; index < max; index += 1) {
    if (touchJson(prev.confluences[index]) !== touchJson(next.confluences[index])) {
      keys.push(`confluence:${index}`);
    }
  }
  const entryChanged =
    String(prev.entry.mode ?? "") !== String(next.entry.mode ?? "") ||
    String(prev.entry.side ?? "") !== String(next.entry.side ?? "") ||
    String(prev.entry.candle_type ?? "") !== String(next.entry.candle_type ?? "") ||
    touchJson(strategyRdMaxWaitForWire(prev.entry.max_wait) ?? null) !==
      touchJson(strategyRdMaxWaitForWire(next.entry.max_wait) ?? null);
  if (entryChanged) keys.push("entry");
  if (String(prev.candleType ?? "") !== String(next.candleType ?? "")) keys.push("timeframe");
  return keys;
}
